import * as React from 'react';
import { useQuery, ApolloError } from '@apollo/client';

import COMPOSER_GROUPS_QUERY from 'chat/queries/ComposerGroupsQuery.gql';
import COMPOSER_ORGS_QUERY from 'chat/queries/ComposerOrgsQuery.gql';
import COMPOSER_GROUPS_FROM_INITIAL_TARGETS_QUERY from 'chat/queries/ComposerGroupsFromInitialTargets.gql';
import {
  ComposerOrgs,
  ComposerOrgs_me_organizations,
} from 'chat/queries/__generated__/ComposerOrgs';
import {
  ComposerGroups,
  ComposerGroups_me_classes,
} from 'chat/queries/__generated__/ComposerGroups';
import {
  ComposerGroupsFromInitialTargets,
  ComposerGroupsFromInitialTargetsVariables,
} from 'chat/queries/__generated__/ComposerGroupsFromInitialTargets';
import { Target } from 'entities/Message';
import { ComposerOrganization } from 'types/typescript/messagingTypes';
import { filterNull } from 'utils/tsUtils';
import ComposerDataLoader, { Props as DataLoaderProps } from './ComposerDataLoader';

type ChildProps = {
  groupsError?: ApolloError;
  groupsLoading?: boolean;
  orgsError?: ApolloError;
  orgsLoading?: boolean;
  organizations?: ComposerOrganization[];
};

type Props = {
  children: (props: ChildProps) => React.ReactElement;
  initialTargets?: Target[];
};

const ComposerDataContainer: React.FC<Props> = ({ children, initialTargets }) => {
  const {
    data: orgsData,
    loading: orgsLoading,
    error: orgsError,
  } = useQuery<ComposerOrgs>(COMPOSER_ORGS_QUERY);
  const {
    data: groupsData,
    loading: groupsLoading,
    error: groupsError,
  } = useQuery<ComposerGroups>(COMPOSER_GROUPS_QUERY);

  const allTargetsAreGroups = initialTargets?.every((target) => target.type === 'group_target');
  const initialTargetGroupsUuids = initialTargets?.map((target) => target.uuid);
  const { data: groupsFromInitialTargetsData, loading: groupsFromInitialTargetsLoading } = useQuery<
    ComposerGroupsFromInitialTargets,
    ComposerGroupsFromInitialTargetsVariables
  >(COMPOSER_GROUPS_FROM_INITIAL_TARGETS_QUERY, {
    variables: { uuids: initialTargetGroupsUuids },
    skip: !allTargetsAreGroups,
  });

  const [orgs, setOrgs] = React.useState<ComposerOrgs_me_organizations[] | undefined>();
  const [orgsWithSendAnnouncementPermission, setOrgsWithSendAnnouncementPermission] =
    React.useState<ComposerOrgs_me_organizations[] | undefined>();
  const [groups, setGroups] = React.useState<ComposerGroups_me_classes[] | undefined>();

  React.useEffect(() => {
    const orgsFromData = orgsData?.me?.organizations;
    setOrgs(
      orgsFromData &&
        orgsFromData.filter((o) => o.type === 'District' && o.permissions.canSendAnnouncement)
    );
    setOrgsWithSendAnnouncementPermission(
      orgsFromData &&
        orgsFromData.filter((o) => {
          return o.permissions.canSendAnnouncement;
        })
    );
  }, [orgsData]);

  React.useEffect(() => {
    const groupsFromInitialTargets = filterNull(
      groupsFromInitialTargetsData?.classes ?? []
    ) as unknown as ComposerGroups_me_classes[];
    if (!groupsLoading && !groupsFromInitialTargetsLoading) {
      setGroups(filterNull([...(groupsData?.me?.classes ?? []), ...groupsFromInitialTargets]));
    }
  }, [groupsData, groupsFromInitialTargetsData, groupsLoading, groupsFromInitialTargetsLoading]);

  const dataLoaderProps: DataLoaderProps = {
    groups,
    organizations: orgs,
    groupsLoading,
    orgsLoading,
    initialTargets,
  };

  return (
    <>
      <ComposerDataLoader {...dataLoaderProps} />
      {children({
        groupsError,
        groupsLoading,
        orgsError,
        orgsLoading,
        organizations: orgsWithSendAnnouncementPermission,
      })}
    </>
  );
};

export default ComposerDataContainer;
